'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { buildClient } from '@datocms/cma-client-browser'
import { useSiteSearch } from 'react-datocms'
import { useSearch } from '@/hooks/useSearch'
import { useSearchHistory } from '@/hooks/useSearchHistory'
import { useDebounce } from '@/hooks/useDebounce'
import { localeToDatoLocale } from '@/lib/locale.utils'

interface UseSearchFormOptions {
	/**
	 * Whether to sync the query with URL search params
	 * @default false
	 */
	syncWithUrl?: boolean
	/**
	 * Whether to initialize from URL query parameter
	 * @default false
	 */
	initializeFromUrl?: boolean
	/**
	 * The language parameter (e.g., 'en', 'fr')
	 */
	lang: string
	/**
	 * Unique identifier for this search instance
	 * @default 'default'
	 */
	instanceId?: string
}

export function useSearchForm(options: UseSearchFormOptions) {
	const {
		syncWithUrl = false,
		initializeFromUrl = false,
		lang,
		instanceId = 'default',
	} = options

	const router = useRouter()
	const searchParams = useSearchParams()
	const datoLang = localeToDatoLocale(lang)

	const [isFocused, setFocused] = useState<boolean>(false)
	const [localQuery, setLocalQuery] = useState<string>('')
	const isInitialized = useRef<boolean>(false)

	const client = buildClient({
		apiToken: process.env.NEXT_PUBLIC_DATOCMS_SITE_SEARCH_TOKEN,
	})

	const query = searchParams.get('q')

	// Initialize local state from URL query parameter (only once on mount)
	useEffect(() => {
		if (initializeFromUrl && !isInitialized.current && query !== null) {
			setLocalQuery(query)
			isInitialized.current = true
		}
	}, [query, initializeFromUrl, instanceId])

	// Debounce the query with a 400ms delay to reduce API calls
	const debouncedQuery = useDebounce(localQuery, 400)

	// Update URL when debounced query changes (only if syncWithUrl is true)
	useEffect(() => {
		if (syncWithUrl && debouncedQuery !== query) {
			// Avoid URL updates during initial load or when query is being cleared
			if (debouncedQuery === '' && query !== null) {
				return
			}

			const params = new URLSearchParams(searchParams.toString())

			if (debouncedQuery) {
				params.set('q', debouncedQuery)
			} else {
				params.delete('q')
			}

			router.replace(`?${params.toString()}`, { scroll: false })
		}
	}, [debouncedQuery, query, router, searchParams, syncWithUrl, instanceId])

	const { state, data, error } = useSiteSearch({
		client,
		fuzzySearch: true,
		initialState: { locale: lang as string },
		highlightMatch: (text, key) => <strong key={key + text}>{text}</strong>,
		buildTriggerId: '31315',
		resultsPerPage: 100,
	})

	const datoSiteSearchLoading = !data && !error

	const input = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (isFocused) input.current?.focus()
	}, [isFocused])

	// Track the current query to avoid unnecessary state.setQuery calls
	const currentQueryRef = useRef<string>('')
	const lastQueryTimeRef = useRef<number>(0)
	const throttleMs = 1000 // Minimum time between requests

	// Create a stable setQuery function with simple throttling
	const setQuery = useCallback(
		(query: string) => {
			const now = Date.now()

			// Simple throttling - only allow one request per throttleMs
			if (now - lastQueryTimeRef.current < throttleMs) {
				return
			}

			lastQueryTimeRef.current = now
			state.setQuery(query)
		},
		[state.setQuery],
	)

	// Set the query in useSiteSearch when debouncedQuery changes
	useEffect(() => {
		if (debouncedQuery !== currentQueryRef.current) {
			currentQueryRef.current = debouncedQuery
			setQuery(debouncedQuery)
		}
	}, [debouncedQuery, setQuery])

	const { loading, products, accessories, resources, dt } = useSearch(
		debouncedQuery,
		lang as string,
	)

	const { searchHistory, addSearchToHistory } = useSearchHistory()

	function close() {
		setFocused(false)
		setLocalQuery('')
	}

	const isResult = useMemo(() => {
		const pn = debouncedQuery.replace(/\s+/g, '')
		const isPartNumber = /^[A-Za-z0-9-]+$/.test(pn)

		return (
			!loading &&
			(data?.pageResults?.filter((r) => r.url.includes(`/${datoLang}/`))
				.length || 0) +
				(debouncedQuery.length > 2 || isPartNumber ? products.length : 0) +
				(debouncedQuery.length > 2 || isPartNumber ? accessories.length : 0) +
				(dt ? 1 : 0) +
				resources.length
		)
	}, [
		data,
		products,
		accessories,
		dt,
		resources,
		debouncedQuery,
		loading,
		datoLang,
	])

	const isLoading = useMemo(
		() => loading || datoSiteSearchLoading,
		[loading, datoSiteSearchLoading],
	)

	// Track previous result count to detect changes
	const previousResultCountRef = useRef<number>(0)
	const lastProcessedQueryRef = useRef<string>('')

	// Add search to history when results are loaded and query is valid
	useEffect(() => {
		// Only process if we have a valid query and search is complete
		if (debouncedQuery.length >= 3 && !loading && !datoSiteSearchLoading) {
			// Check if this is a new query or if result count has changed
			const isNewQuery = debouncedQuery !== lastProcessedQueryRef.current
			const resultCountChanged = isResult !== previousResultCountRef.current

			if (isNewQuery || resultCountChanged) {
				// Check if this query already exists in history
				const queryStoredSearch = searchHistory.find(
					(q) => q.query === debouncedQuery,
				)

				// Only add if the result count is different or the query doesn't exist
				if (queryStoredSearch?.resultCount !== isResult) {
					// Defer the state update to avoid the "setState during render" warning
					setTimeout(() => {
						addSearchToHistory(debouncedQuery, isResult)
					}, 0)
				}

				// Update our tracking refs
				lastProcessedQueryRef.current = debouncedQuery
				previousResultCountRef.current = isResult
			}
		}
	}, [
		debouncedQuery,
		isResult,
		loading,
		searchHistory,
		addSearchToHistory,
		data,
		products,
		accessories,
		resources,
		dt,
		datoSiteSearchLoading,
		instanceId,
	])

	// Handle search history click
	const handleSearchHistoryClick = (query: string) => {
		setLocalQuery(query)
	}

	return {
		// State
		isFocused,
		setFocused,
		localQuery,
		setLocalQuery,
		debouncedQuery,
		isResult,
		isLoading,
		datoSiteSearchLoading,

		// Refs
		input,

		// Data
		data: data?.pageResults.filter((d) => d.url.includes(`/${datoLang}/`)),
		products: (() => {
			const pn = debouncedQuery.replace(/\s+/g, '')
			const isPartNumber = /^[A-Za-z0-9-]+$/.test(pn)
			return debouncedQuery.length > 2 || isPartNumber ? products : []
		})(),
		accessories: (() => {
			const pn = debouncedQuery.replace(/\s+/g, '')
			const isPartNumber = /^[A-Za-z0-9-]+$/.test(pn)
			return debouncedQuery.length > 2 || isPartNumber ? accessories : []
		})(),
		resources,
		dt,

		// Search history
		searchHistory,
		handleSearchHistoryClick,

		// Actions
		close,
	}
}
